/**
 * Wave BD — Global progress strip visible on EVERY page of the workspace.
 *
 * Polls `/api/projects/{id}/stages/active` every 4 s and surfaces:
 *   • In-progress stages (B.1 sim, B.6 HAZOP, etc.) — shown as pills
 *   • PFD background generation queue (current sheet + percent + ETA)
 *
 * The component is mounted by `Workspace.jsx` at the top of the layout so
 * users always see what is running, regardless of which module/tab they
 * navigated to. This delivers the requirement that "background tasks must
 * persist when navigating away from any module, and progress bars must be
 * visible on every stage gate wizard sheet".
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, PauseCircle, PlayCircle, XCircle } from "lucide-react";
import api from "../lib/api";

const fmtDuration = (sec) => {
  if (!sec || sec <= 0) return null;
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s ? `${m}m ${s}s` : `${m}m`;
};

export default function GlobalStageProgressBar({ projectId, onJumpStage }) {
  const [data, setData] = useState({ stages: [], pfd: { active: false } });
  const [, setTick] = useState(0);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/stages/active`);
      setData(r.data || { stages: [], pfd: { active: false } });
    } catch { /* ignore — keep last data */ }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return undefined;
    load();
    timerRef.current = setInterval(load, 4000);
    // Live "elapsed time" tick once a second so the UI feels responsive
    const elapsedTimer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      clearInterval(elapsedTimer);
    };
  }, [projectId, load]);

  const stages = data.stages || [];
  const pfd = data.pfd || { active: false };
  const hasAny = stages.length > 0 || pfd.active;
  if (!hasAny) return null;

  const pausePfd = async () => {
    try { await api.post(`/projects/${projectId}/pfd/pause`); }
    finally { load(); }
  };
  const resumePfd = async () => {
    try { await api.post(`/projects/${projectId}/pfd/resume`); }
    finally { load(); }
  };
  const cancelPfd = async () => {
    if (!window.confirm("Cancel PFD generation queue? Already-completed sheets are kept.")) return;
    try { await api.post(`/projects/${projectId}/pfd/cancel`); }
    finally { load(); }
  };

  return (
    <div data-testid="global-stage-progress-bar"
         style={{
           background: "linear-gradient(90deg, rgba(34,211,238,0.10), rgba(106,170,221,0.10))",
           borderBottom: "1px solid var(--pf-border, #1e293b)",
           padding: "8px 18px", display: "flex", alignItems: "center",
           gap: 14, fontSize: 11, color: "var(--pf-text, #e2e8f0)",
           fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
         }}>
      <Loader2 size={14}
               style={{ color: "var(--pf-spark, #22d3ee)" }}
               className="pf-spin"/>
      <span style={{ fontWeight: 700, color: "var(--pf-spark, #22d3ee)" }}>
        BACKGROUND TASKS
      </span>

      {/* In-progress stage pills */}
      {stages.map((s) => {
        const startedAt = s.started_at ? new Date(s.started_at) : null;
        const elapsed = startedAt
          ? Math.round((Date.now() - startedAt.getTime()) / 1000)
          : null;
        const prog = s.progress || {};
        const pct = prog.percent != null ? prog.percent : null;
        return (
          <button
            key={s.stage_code}
            data-testid={`global-active-stage-${s.stage_code}`}
            onClick={() => onJumpStage?.(s.stage_code)}
            style={{
              background: "rgba(34,211,238,0.12)",
              border: "1px solid rgba(34,211,238,0.4)",
              borderRadius: 8, padding: "4px 10px",
              color: "var(--pf-text, #e2e8f0)", cursor: "pointer",
              fontSize: 11, fontFamily: "inherit",
              display: "inline-flex", flexDirection: "column",
              alignItems: "stretch", gap: 3, minWidth: pct != null ? 240 : 140,
            }}
            title={prog.message || `Click to jump to ${s.stage_code}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontWeight: 700, color: "var(--pf-spark, #22d3ee)" }}>
                {s.stage_code}
              </span>
              <span style={{ color: "var(--pf-text-muted, #94a3b8)",
                               whiteSpace: "nowrap", overflow: "hidden",
                               textOverflow: "ellipsis", maxWidth: 180 }}>
                {(prog.message || s.stage_name || "").slice(0, 42)}
              </span>
              {pct != null && (
                <span data-testid={`global-stage-${s.stage_code}-percent`}
                      style={{ marginLeft: "auto", fontWeight: 700,
                                 color: "var(--pf-spark, #22d3ee)" }}>
                  {Math.round(pct)}%
                </span>
              )}
              {pct == null && elapsed !== null && (
                <span style={{ marginLeft: "auto",
                                color: "var(--pf-text-muted, #94a3b8)" }}>
                  {fmtDuration(elapsed)}
                </span>
              )}
            </div>
            {pct != null && (
              <div style={{ height: 3,
                             background: "rgba(255,255,255,0.06)",
                             borderRadius: 2 }}>
                <div data-testid={`global-stage-${s.stage_code}-bar`}
                     style={{
                        width: `${Math.max(2, Math.min(100, pct))}%`,
                        height: "100%",
                        background: "linear-gradient(90deg, #22d3ee, #0ea5e9)",
                        borderRadius: 2, transition: "width 450ms ease",
                     }}/>
              </div>
            )}
            {pct != null && prog.current_step != null && prog.total_steps != null && (
              <div style={{ fontSize: 9.5, color: "var(--pf-text-muted, #94a3b8)",
                              letterSpacing: "0.03em" }}>
                step {prog.current_step}/{prog.total_steps}
                {elapsed !== null && ` · ${fmtDuration(elapsed)}`}
              </div>
            )}
          </button>
        );
      })}

      {/* PFD generation block */}
      {pfd.active && (
        <div data-testid="global-pfd-progress"
             style={{
               flex: 1, display: "flex", alignItems: "center", gap: 10,
               background: "rgba(245,158,11,0.06)",
               border: "1px solid rgba(245,158,11,0.3)",
               borderRadius: 6, padding: "4px 12px",
             }}>
          <span style={{ fontWeight: 700, color: "#f59e0b" }}>PFD QUEUE</span>
          <span style={{ color: "var(--pf-text-muted, #94a3b8)" }}>
            {pfd.current_job?.sheet_id || "—"}
          </span>
          <span style={{ flex: 1, color: "var(--pf-text, #e2e8f0)" }}>
            {pfd.progress?.message || "Working…"}
          </span>
          <div style={{
            width: 160, height: 5,
            background: "rgba(255,255,255,0.06)", borderRadius: 3,
          }}>
            <div data-testid="global-pfd-progress-bar"
                 style={{
                   width: `${pfd.progress?.percent || 0}%`, height: "100%",
                   background: "linear-gradient(90deg, #f59e0b, #fbbf24)",
                   borderRadius: 3, transition: "width 350ms ease",
                 }}/>
          </div>
          <span style={{ color: "var(--pf-text-muted, #94a3b8)", fontWeight: 700 }}>
            {pfd.progress?.current || 0}/{pfd.progress?.total || 0}
            {" · "}
            <span data-testid="global-pfd-percent">
              {pfd.progress?.percent || 0}%
            </span>
          </span>
          {pfd.progress?.eta_seconds != null && pfd.progress.eta_seconds > 0 && (
            <span style={{ color: "var(--pf-text-muted, #94a3b8)" }}>
              ETA {fmtDuration(pfd.progress.eta_seconds)}
            </span>
          )}
          {pfd.paused ? (
            <button data-testid="global-pfd-resume" onClick={resumePfd}
                    style={iconBtnStyle("#22c55e")}
                    title="Resume PFD generation">
              <PlayCircle size={14}/>
            </button>
          ) : (
            <button data-testid="global-pfd-pause" onClick={pausePfd}
                    style={iconBtnStyle("#f59e0b")}
                    title="Pause PFD generation">
              <PauseCircle size={14}/>
            </button>
          )}
          <button data-testid="global-pfd-cancel" onClick={cancelPfd}
                  style={iconBtnStyle("#ef4444")}
                  title="Cancel PFD generation">
            <XCircle size={14}/>
          </button>
        </div>
      )}
    </div>
  );
}

function iconBtnStyle(color) {
  return {
    background: "transparent", border: 0, color, cursor: "pointer",
    padding: 2, display: "inline-flex", alignItems: "center",
  };
}
